import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useLang } from "@/contexts/LanguageContext";
import { LANGS } from "@/i18n/translations";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent,
  DropdownMenuItem, DropdownMenuSeparator
} from "@/components/ui/dropdown-menu";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import {
  LogIn, LogOut, LayoutDashboard, Shield, Briefcase, Megaphone, Menu, Globe, Settings, Moon, Sun,
  Home, Newspaper, Car, User, ChevronLeft, Palette, Languages, UserPlus, Bell, Info, FileText, MapPin, Star, Gamepad2
} from "lucide-react";
import HeaderSupportButton from "@/components/HeaderSupportButton";
import { useState, useEffect } from "react";
import * as React from "react";
import { toast } from "sonner";
import { NotificationBell } from "@/components/NotificationBell";
import { RatingButton } from "@/components/RatingButton";
import { useTheme } from "@/contexts/ThemeContext";
import NearbyShopsSheet from "@/components/NearbyShopsSheet";
import { InstallAppCompact } from "@/components/InstallApp";

function ThemeToggle() {
  const { theme, toggle } = useTheme();
  const isDark = theme === "dark";
  return (
    <Button
      variant="ghost"
      size="icon"
      onClick={toggle}
      className="size-9 rounded-full"
      title={isDark ? "الوضع النهاري" : "الوضع الليلي"}
      aria-label="Toggle theme"
      data-testid="theme-toggle-btn"
    >
      {isDark ? <Sun className="size-5 text-amber-400" /> : <Moon className="size-5 text-slate-700" />}
    </Button>
  );
}

function LanguageSwitcher() {
  const { lang, setLang, t } = useLang();
  const current = LANGS[lang] || LANGS.ar;
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="sm" className="gap-1.5" data-testid="lang-switcher-btn">
          <Globe className="size-4" />
          <span className="text-xs font-bold hidden sm:inline">{current.native}</span>
          <span className="text-base">{current.flag}</span>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="w-44">
        {Object.values(LANGS).map((L) => (
          <DropdownMenuItem
            key={L.code}
            onClick={() => setLang(L.code)}
            data-testid={`lang-set-${L.code}`}
            className={lang === L.code ? "bg-primary/10 font-bold" : ""}
            lang={L.code}
          >
            <span className="text-xl ms-2">{L.flag}</span>
            <span>{L.native}</span>
            {lang === L.code && <span className="ms-auto">✓</span>}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

export function Header() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [nearbyOpen, setNearbyOpen] = useState(false);
  const isDriver = user && user.role === "delivery_driver";
  const { t } = useLang();

  // Listen for global "open-mobile-drawer" event (dispatched from BottomNav account tab)
  React.useEffect(() => {
    const openHandler = () => setMobileOpen(true);
    window.addEventListener("kh-open-drawer", openHandler);
    return () => window.removeEventListener("kh-open-drawer", openHandler);
  }, []);

  // Listen for global "kh-open-nearby" event so any component can trigger it
  React.useEffect(() => {
    const h = () => setNearbyOpen(true);
    window.addEventListener("kh-open-nearby", h);
    return () => window.removeEventListener("kh-open-nearby", h);
  }, []);

  // Driver-only minimal header: logo + bell + name + logout. No browsing nav.
  if (isDriver) {
    return (
      <header className="glass sticky top-0 z-50" style={{ paddingTop: "env(safe-area-inset-top, 0px)" }}>
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <Link to="/delivery-dashboard" data-testid="header-logo" className="flex items-center gap-3">
            <img
              src="/hood-icon.png"
              alt="هود"
              className="size-11 rounded-xl object-cover shadow-md"
            />
            <div className="flex flex-col leading-tight">
              <span className="font-black text-lg">هود</span>
              <span className="text-xs text-muted-foreground">لوحة الدلفري</span>
            </div>
          </Link>
          <div className="flex items-center gap-2">
            <NotificationBell />
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" className="gap-2" data-testid="user-menu-btn">
                  <User className="size-4" />
                  <span className="hidden sm:inline">{user.name}</span>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start" className="w-56">
                <DropdownMenuItem onClick={() => navigate("/account")} data-testid="menu-account">
                  <Settings className="size-4 ms-2" /> إدارة حسابي
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={logout} data-testid="menu-logout">
                  <LogOut className="size-4 ms-2" /> تسجيل الخروج
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </header>
    );
  }

  return (
    <header className="glass sticky top-0 z-50" style={{ paddingTop: "env(safe-area-inset-top, 0px)" }}>
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Link to="/" data-testid="header-logo" className="flex items-center gap-2.5 group">
            <div className="relative">
              <img
                src="/hood-icon.png"
                alt="هود"
                className="size-10 rounded-2xl object-cover ring-1 ring-primary/25 group-hover:ring-primary/50 transition"
              />
              <span className="absolute -bottom-0.5 -end-0.5 size-3 rounded-full bg-primary border-2 border-background" />
            </div>
            <div className="flex flex-col leading-tight">
              <span className="font-black text-base">{t("app.name")}</span>
              <span className="text-[10px] text-muted-foreground font-medium">{t("app.tagline")}</span>
            </div>
          </Link>
        </div>

        <nav className="hidden md:flex items-center gap-1 text-sm">
          <Link to="/" className="px-3 py-2 rounded-full hover:bg-muted transition font-medium" data-testid="nav-home">{t("nav.home")}</Link>
          <Link to="/news" className="px-3 py-2 rounded-full hover:bg-muted transition font-medium" data-testid="nav-news">{t("nav.news")}</Link>
          <Link to="/jobs" className="px-3 py-2 rounded-full hover:bg-muted transition font-medium" data-testid="nav-jobs">{t("nav.jobs")}</Link>
          <Link to="/promotions" className="px-3 py-2 rounded-full hover:bg-muted transition font-medium" data-testid="nav-promotions">{t("nav.promotions")}</Link>
          <Link to="/#taxi-services" className="px-3 py-2 rounded-full hover:bg-muted transition font-medium" data-testid="nav-taxi">{t("nav.taxi")}</Link>
        </nav>

        <div className="flex items-center gap-1.5">
          {/* Desktop-only visible controls (mobile moves them into the drawer) */}
          <div className="hidden md:flex items-center gap-1.5">
            <LanguageSwitcher />
            <ThemeToggle />
          </div>
          {/* Nearby shops trigger — opens the bottom sheet */}
          <Button
            variant="ghost"
            size="icon"
            onClick={() => setNearbyOpen(true)}
            className="size-10 rounded-full relative hover:bg-primary/10"
            aria-label="المتاجر القريبة"
            title="المتاجر القريبة"
            data-testid="header-nearby-btn"
          >
            <MapPin className="size-5 text-primary" />
          </Button>
          {/* News shortcut — icon + tiny label */}
          <Link
            to="/news"
            data-testid="header-news-btn"
            aria-label="الأخبار"
            title="الأخبار"
            className="flex flex-col items-center justify-center gap-0.5 h-10 px-2 rounded-full hover:bg-primary/10 transition"
          >
            <Newspaper className="size-5 text-primary" />
            <span className="text-[9px] leading-none font-bold text-primary/90">الأخبار</span>
          </Link>
          <HeaderSupportButton />
          {/* Notifications bell — shown in place of the old mobile hamburger */}
          {user && user.id ? (
            <NotificationBell />
          ) : (
            <Button
              variant="ghost"
              size="icon"
              onClick={() => navigate("/login")}
              className="md:hidden size-10 rounded-full relative"
              aria-label="الإشعارات"
              data-testid="mobile-notif-bell"
            >
              <Bell className="size-5" />
            </Button>
          )}
          {user && user.id ? (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" className="gap-2 rounded-full border-border/60 h-9 hidden md:inline-flex" data-testid="user-menu-btn">
                  <User className="size-4" />
                  <span className="hidden sm:inline text-sm">{user.name}</span>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start" className="w-56">
                <DropdownMenuItem onClick={() => navigate("/dashboard")} data-testid="menu-dashboard">
                  <LayoutDashboard className="size-4 ms-2" /> {t("nav.dashboard")}
                </DropdownMenuItem>
                {user.role === "shop_owner" && (
                  <DropdownMenuItem onClick={() => navigate("/shop-dashboard")} data-testid="menu-shop">
                    <Briefcase className="size-4 ms-2" /> {t("nav.shop_dashboard")}
                  </DropdownMenuItem>
                )}
                {user.role === "admin" && (
                  <DropdownMenuItem onClick={() => navigate("/admin")} data-testid="menu-admin">
                    <Shield className="size-4 ms-2" /> {t("nav.admin")}
                  </DropdownMenuItem>
                )}
                <DropdownMenuItem onClick={() => navigate("/promotions/new")} data-testid="menu-promo">
                  <Megaphone className="size-4 ms-2" /> {t("nav.promotions")}
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => navigate("/my-account")} data-testid="menu-my-account">
                  <User className="size-4 ms-2" /> إدارة حسابي
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={logout} data-testid="menu-logout">
                  <LogOut className="size-4 ms-2" /> {t("nav.logout")}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          ) : (
            <>
              <Button variant="ghost" onClick={() => navigate("/login")} data-testid="header-login-btn" className="gap-2 rounded-full h-9 text-sm hidden md:inline-flex">
                <LogIn className="size-4" /> {t("nav.login")}
              </Button>
              <Button onClick={() => navigate("/register")} data-testid="header-register-btn" className="bg-primary hover:bg-primary/90 rounded-full h-9 px-4 text-sm font-bold shadow-md glow-primary-soft hidden md:inline-flex">
                {t("nav.register")}
              </Button>
            </>
          )}
          {/* Mobile drawer (Sheet trigger is hidden — it is opened via the BottomNav "account" tab event) */}
          <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
            <SheetTrigger asChild>
              <button type="button" aria-hidden className="hidden" data-testid="hidden-drawer-trigger" />
            </SheetTrigger>
            <MobileDrawer user={user} logout={logout} onClose={() => setMobileOpen(false)} />
          </Sheet>
        </div>
      </div>
      {/* Global nearby-shops sheet triggered from the header pin icon */}
      <NearbyShopsSheet open={nearbyOpen} onClose={() => setNearbyOpen(false)} />
    </header>
  );
}

/* ================= Mobile Premium Drawer ================= */
function MobileDrawer({ user, logout, onClose }) {
  const navigate = useNavigate();
  const { t, lang, setLang } = useLang();
  const { theme, toggle: toggleTheme } = useTheme();
  const isDark = theme === "dark";
  const currentLang = LANGS[lang] || LANGS.ar;
  const [rateOpen, setRateOpen] = React.useState(false);

  const go = (path) => { onClose(); navigate(path); };

  return (
    <SheetContent side="right" className="w-[85%] sm:w-[380px] p-0 border-l border-border/40 bg-slate-950 text-white flex flex-col overflow-hidden [&>button.absolute]:hidden">
      {/* Ambient glow */}
      <div className="pointer-events-none absolute -top-20 -end-16 size-64 rounded-full bg-primary/15 blur-3xl" />

      {/* --- Profile Header --- */}
      <div className="relative p-5 pt-safe border-b border-white/10">
        {user?.id ? (
          <div>
            <div className="flex items-center gap-3">
              {user.avatar ? (
                <img src={user.avatar} alt={user.name} className="size-14 rounded-2xl object-cover border border-primary/40 glow-primary-soft" />
              ) : (
                <div className="size-14 rounded-2xl bg-primary/20 border border-primary/40 flex items-center justify-center text-primary text-xl font-black shrink-0 glow-primary-soft">
                  {(user.name || "?").trim().charAt(0)}
                </div>
              )}
              <div className="min-w-0 flex-1">
                <div className="font-bold text-base truncate">{user.name || "بدون اسم"}</div>
                <div className="text-xs text-white/60 truncate">
                  {/* Root admin (username="admin") has no email — show just the role. */}
                  {(user.username === "admin") ? "مدير النظام الرئيسي" : (user.email || user.phone || "—")}
                </div>
                <div className="mt-1 inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-primary/15 border border-primary/30 text-[10px] font-bold text-primary uppercase tracking-wide">
                  {user.role === "shop_owner" ? "صاحب محل"
                    : user.role === "admin" ? "مسؤول"
                    : user.role === "delivery_driver" ? "دلفري"
                    : "زبون"}
                </div>
              </div>
            </div>
            <button
              type="button"
              onClick={() => go("/my-account")}
              data-testid="drawer-manage-account"
              className="mt-4 w-full h-11 rounded-full bg-primary hover:bg-primary/90 text-primary-foreground font-bold text-sm flex items-center justify-center gap-2 shadow-lg glow-primary-soft transition"
            >
              <Settings className="size-4" /> إدارة حسابي
            </button>
          </div>
        ) : (
          <div>
            <div className="text-lg font-black mb-1">مرحباً بك 👋</div>
            <p className="text-sm text-white/60 mb-4">سجّل الدخول للوصول لجميع الميزات</p>
            <div className="grid grid-cols-2 gap-2">
              <button
                type="button"
                onClick={() => go("/login")}
                data-testid="drawer-login-btn"
                className="h-11 rounded-full bg-primary hover:bg-primary/90 text-primary-foreground font-bold text-sm flex items-center justify-center gap-1.5 shadow-lg glow-primary-soft transition"
              >
                <LogIn className="size-4" /> دخول
              </button>
              <button
                type="button"
                onClick={() => go("/register")}
                data-testid="drawer-register-btn"
                className="h-11 rounded-full bg-white/5 hover:bg-white/10 text-white border border-white/15 font-bold text-sm flex items-center justify-center gap-1.5 transition"
              >
                <UserPlus className="size-4" /> إنشاء حساب
              </button>
            </div>
          </div>
        )}
      </div>

      {/* --- Scrollable content --- */}
      <div className="flex-1 overflow-y-auto p-3 space-y-6">

        {/* Navigation */}
        <DrawerSection title="التصفح">
          <DrawerItem icon={Home} label={t("nav.home") || "الرئيسية"} onClick={() => go("/")} testId="drawer-nav-home" />
          <DrawerItem icon={Newspaper} label={t("nav.news") || "الأخبار"} onClick={() => go("/news")} testId="drawer-nav-news" />
          <DrawerItem icon={Megaphone} label={t("nav.promotions") || "الإعلانات"} onClick={() => go("/promotions")} testId="drawer-nav-promotions" />
          <DrawerItem icon={Car} label={t("nav.taxi") || "التكسي"} onClick={() => go("/#taxi-services")} testId="drawer-nav-taxi" />
          <DrawerItem icon={Briefcase} label={t("nav.jobs") || "الوظائف"} onClick={() => go("/jobs")} testId="drawer-nav-jobs" />
          <DrawerItem icon={Gamepad2} label="🎮 الألعاب" onClick={() => go("/games")} testId="drawer-nav-games" accent />
        </DrawerSection>

        {/* Role-specific quick access */}
        {user?.id && (
          <DrawerSection title="حسابي">
            {user.role === "shop_owner" && (
              <DrawerItem icon={Briefcase} label={t("nav.shop_dashboard") || "لوحة المحل"} onClick={() => go("/shop-dashboard")} testId="drawer-my-shop" accent />
            )}
            {user.role === "admin" && (
              <DrawerItem icon={Shield} label={t("nav.admin") || "لوحة الإدارة"} onClick={() => go("/admin")} testId="drawer-admin" accent />
            )}
            {user.role === "delivery_driver" && (
              <DrawerItem icon={LayoutDashboard} label="لوحة الدلفري" onClick={() => go("/delivery-dashboard")} testId="drawer-driver" accent />
            )}
            <DrawerItem icon={LayoutDashboard} label={t("nav.dashboard") || "الطلبات"} onClick={() => go("/dashboard")} testId="drawer-orders" />
            <DrawerItem icon={Megaphone} label="إعلان جديد" onClick={() => go("/promotions/new")} testId="drawer-new-promo" />
            <DrawerItem icon={Settings} label="تعديل بيانات الحساب" onClick={() => go("/my-account")} testId="drawer-edit-account" />
            <DrawerItem icon={Bell} label="تعديل تفضيلات الإشعارات" onClick={() => go("/my-account")} testId="drawer-notif-prefs" />
          </DrawerSection>
        )}

        {/* Preferences */}
        <DrawerSection title="التفضيلات">
          {/* Language row (opens dropdown) */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button type="button" className="w-full flex items-center gap-3 px-3 py-3 rounded-2xl hover:bg-white/5 transition" data-testid="drawer-lang-btn">
                <div className="size-10 rounded-xl bg-white/5 border border-white/10 flex items-center justify-center shrink-0">
                  <Languages className="size-5 text-white/80" />
                </div>
                <div className="flex-1 text-start">
                  <div className="text-sm font-medium">اللغة</div>
                  <div className="text-xs text-white/50">{currentLang.native} {currentLang.flag}</div>
                </div>
                <ChevronLeft className="size-4 text-white/40 rtl:rotate-180" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start" className="w-56">
              {Object.values(LANGS).map((L) => (
                <DropdownMenuItem key={L.code} onClick={() => setLang(L.code)} data-testid={`drawer-lang-${L.code}`} className={lang === L.code ? "bg-primary/10 text-primary font-bold" : ""}>
                  <span className="me-2 text-lg">{L.flag}</span> {L.native}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>

          {/* Theme toggle row */}
          <button type="button" onClick={toggleTheme} data-testid="drawer-theme-toggle" className="w-full flex items-center gap-3 px-3 py-3 rounded-2xl hover:bg-white/5 transition">
            <div className="size-10 rounded-xl bg-white/5 border border-white/10 flex items-center justify-center shrink-0">
              {isDark ? <Sun className="size-5 text-amber-400" /> : <Moon className="size-5 text-slate-300" />}
            </div>
            <div className="flex-1 text-start">
              <div className="text-sm font-medium">{isDark ? "الوضع الداكن" : "الوضع الفاتح"}</div>
              <div className="text-xs text-white/50">{isDark ? "اضغط للتبديل للنهاري" : "اضغط للتبديل للليلي"}</div>
            </div>
            {/* Toggle pill */}
            <div className={`relative w-11 h-6 rounded-full transition ${isDark ? "bg-primary" : "bg-white/20"}`}>
              <div className={`absolute top-0.5 size-5 rounded-full bg-white transition-all shadow ${isDark ? "start-0.5" : "end-0.5"}`} />
            </div>
          </button>
        </DrawerSection>

        {/* Install the app + owner CTA */}
        <DrawerSection title="حمّل التطبيق">
          <div className="px-2">
            <InstallAppCompact />
          </div>
        </DrawerSection>

        {/* Info */}
        <DrawerSection title="حول التطبيق">
          <DrawerItem icon={Star} label="التقييم والملاحظات" onClick={() => { onClose(); setRateOpen(true); }} testId="drawer-rate" accent />
          <DrawerItem icon={Info} label="من نحن" onClick={() => go("/about")} testId="drawer-about" />
          <DrawerItem icon={FileText} label="الشروط والأحكام" onClick={() => go("/terms")} testId="drawer-terms" />
          <DrawerNotificationsToggle />
        </DrawerSection>
      </div>

      {/* --- Logout footer --- */}
      {user?.id && (
        <div className="p-3 border-t border-white/10 pb-safe">
          <button
            type="button"
            onClick={() => { onClose(); logout(); }}
            data-testid="drawer-logout"
            className="w-full h-11 rounded-full bg-rose-500/10 hover:bg-rose-500/20 text-rose-400 font-bold text-sm flex items-center justify-center gap-2 border border-rose-500/20 transition"
          >
            <LogOut className="size-4" /> تسجيل الخروج
          </button>
        </div>
      )}

      {/* Hidden controlled RatingButton — opens when user taps "التقييم والملاحظات" in drawer */}
      <RatingButton hideTrigger externalOpen={rateOpen} onExternalOpenChange={setRateOpen} />
    </SheetContent>
  );
}

function DrawerSection({ title, children }) {
  return (
    <div>
      <div className="text-[11px] font-bold uppercase tracking-widest text-white/40 px-3 mb-1.5">{title}</div>
      <div className="space-y-0.5">{children}</div>
    </div>
  );
}

function DrawerItem({ icon: Icon, label, onClick, testId, accent = false }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testId}
      className={`w-full flex items-center gap-3 px-3 py-3 rounded-2xl transition ${accent ? "bg-primary/10 hover:bg-primary/15 border border-primary/25" : "hover:bg-white/5"}`}
    >
      <div className={`size-10 rounded-xl border flex items-center justify-center shrink-0 ${accent ? "bg-primary/20 border-primary/40" : "bg-white/5 border-white/10"}`}>
        <Icon className={`size-5 ${accent ? "text-primary" : "text-white/80"}`} />
      </div>
      <div className="flex-1 text-start text-sm font-medium">{label}</div>
      <ChevronLeft className="size-4 text-white/30 rtl:rotate-180" />
    </button>
  );
}


/**
 * DrawerNotificationsToggle — an inline switch that lets the user enable /
 * disable push notifications directly from the drawer. Uses the real OS
 * permission state (Notification.permission) plus our push-subscription
 * helpers so the toggle reflects reality.
 *
 * States:
 *   granted    → toggle ON, tapping OFF calls unsubscribeFromPush()
 *   default    → toggle OFF, tapping ON calls Notification.requestPermission()
 *                followed by subscribeToPush()
 *   denied     → toggle OFF and disabled, with helper text explaining that
 *                the user must re-enable from OS settings
 */
function DrawerNotificationsToggle() {
  const [isNative, setIsNative] = useState(false);
  const [perm, setPerm] = useState(
    typeof Notification !== "undefined" ? Notification.permission : "unsupported"
  );
  const [busy, setBusy] = useState(false);
  const isOn = perm === "granted";
  const isDenied = perm === "denied";
  const unsupported = perm === "unsupported";

  // Detect native platform + read the OS permission via PushNotifications API
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { isNativePlatform } = await import("@/lib/native-push");
        const native = isNativePlatform();
        if (cancelled) return;
        setIsNative(native);
        if (native) {
          // On iOS/Android, ignore the Web Notification API and read from Capacitor
          const { PushNotifications } = await import("@capacitor/push-notifications");
          const status = await PushNotifications.checkPermissions();
          if (cancelled) return;
          // Capacitor returns 'granted' | 'denied' | 'prompt' | 'prompt-with-rationale'
          const p = status?.receive === "granted"
            ? "granted"
            : status?.receive === "denied"
              ? "denied"
              : "default";
          setPerm(p);
        } else if (typeof Notification !== "undefined") {
          setPerm(Notification.permission);
        }
      } catch {
        // Fall back to Web API
        if (typeof Notification !== "undefined") setPerm(Notification.permission);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const toggle = async () => {
    if (busy) return;
    setBusy(true);
    try {
      if (isNative) {
        // ===== iOS / Android native path =====
        if (!isOn) {
          try {
            const { initNativePush } = await import("@/lib/native-push");
            const res = await initNativePush();
            if (res.permission === "android_permission_only") {
              setPerm("granted");
              toast.success("✅ تم منح إذن الإشعارات");
            } else if (res.permission === "android_disabled") {
              toast.info(
                "الإشعارات على الأندرويد قيد الإعداد — ستصلك تحديثات قريباً 🔔",
                { duration: 5000 }
              );
            } else if (res.permission === "granted") {
              setPerm("granted");
              toast.success("✅ تم تفعيل الإشعارات");
            } else if (res.permission === "denied") {
              setPerm("denied");
              toast.error("رُفض الإذن — فعّله من إعدادات الجهاز");
            } else {
              toast.info("لم يُمنح الإذن بعد");
            }
          } catch (e) {
            toast.error(e?.message || "تعذّر تفعيل الإشعارات");
          }
        } else {
          // Can't programmatically revoke OS permission on iOS — guide user.
          toast.info("لإيقاف الإشعارات، افتح الإعدادات → هود → الإشعارات");
        }
      } else {
        // ===== Web / PWA path =====
        if (!isOn) {
          if (typeof Notification === "undefined") return;
          const result = await Notification.requestPermission();
          setPerm(result);
          if (result === "granted") {
            try {
              const push = await import("@/lib/push");
              if (push.subscribeToPush) await push.subscribeToPush();
            } catch { /* ignore */ }
            toast.success("✅ تم تفعيل الإشعارات");
          } else if (result === "denied") {
            toast.error("رُفض الإذن — فعّله من إعدادات الجهاز");
          }
        } else {
          try {
            const push = await import("@/lib/push");
            if (push.unsubscribeFromPush) await push.unsubscribeFromPush();
          } catch { /* ignore */ }
          toast.info("تم إيقاف الإشعارات — لإعادة التشغيل استخدم نفس الزر");
          setPerm("default");
        }
      }
    } finally { setBusy(false); }
  };

  const helper = (!isNative && unsupported)
    ? "المتصفح لا يدعم الإشعارات"
    : isDenied
      ? "مرفوض من إعدادات الجهاز — فعّله يدوياً"
      : isOn
        ? "ستصلك الأخبار والطلبات فوراً"
        : "اضغط لتفعيل الإشعارات على هذا الجهاز";

  const disabled = busy || (!isNative && unsupported) || isDenied;

  return (
    <div
      className={`w-full flex items-center gap-3 px-3 py-3 rounded-2xl transition ${
        isOn ? "bg-emerald-500/10 border border-emerald-500/25" : "bg-white/5 hover:bg-white/10"
      } ${!isNative && unsupported ? "opacity-60" : ""}`}
      data-testid="drawer-notifs-toggle-wrapper"
    >
      <div className={`size-10 rounded-xl border flex items-center justify-center shrink-0 ${
        isOn ? "bg-emerald-500/20 border-emerald-500/40" : "bg-white/5 border-white/10"
      }`}>
        <Bell className={`size-5 ${isOn ? "text-emerald-400" : "text-white/80"}`} />
      </div>
      <div className="flex-1 min-w-0 text-start">
        <div className="text-sm font-medium">الإشعارات</div>
        <div className="text-[11px] text-white/50 leading-tight mt-0.5">{helper}</div>
      </div>
      {/* Custom switch (no external dep) */}
      <button
        type="button"
        onClick={toggle}
        disabled={disabled}
        role="switch"
        aria-checked={isOn}
        aria-label={isOn ? "إيقاف الإشعارات" : "تشغيل الإشعارات"}
        data-testid="drawer-notifs-toggle"
        className={`relative shrink-0 w-12 h-7 rounded-full transition-colors ${
          isOn ? "bg-emerald-500" : "bg-white/15"
        } ${disabled ? "opacity-60 cursor-not-allowed" : ""}`}
      >
        <span
          className={`absolute top-0.5 size-6 rounded-full bg-white shadow transition-all ${
            isOn ? "start-0.5" : "end-0.5"
          }`}
        />
      </button>
    </div>
  );
}
